import { StatsGranularity, StatsResolution } from 'components/lib/stats/constants';
import * as firebase from 'firebase';
import { DateTime, Duration } from 'luxon';
import firebaseApp from '../firebase';
import { paths } from '../paths';
import { ICheckout } from '../profile/model';
import {
  setStatAllTime,
  setStatsDataDays,
  setStatsDataWeeks,
  setStatsDuration,
  setStatsLoadError,
  setStatsLoadIsBusy,
  setStatToday,
} from './actions';
import { IStatsState } from './model';

type Reference = firebase.database.Reference;
type DataSnapshot = firebase.database.DataSnapshot;

export function getRecentReadIDs() {
  const RECENT_READ_ID_COUNT = 5;
  const fallbackIsbns = [
    '9788282821094',
    '9788282821117',
    '9788241913105',
    '9788283040319',
    '9788205495661',
  ];
  const INDEX_ORDER = [2, 1, 3, 0, 4];

  return (dispatch: any, getState: any) => {
    const { profile } = getState();
    const prioritizedISBNs: string[] = []; // [a,b,c,d,e]
    const results: string[] = []; // [d,b,a,c,e]
    const usedISBNs = {};

    if (profile && profile.checkouts) {
      profile.checkouts.sort((a: ICheckout, b: ICheckout) => {
        if (a.create_time && b.create_time) {
          if (a.create_time > b.create_time) {
            return -1;
          }
          if (a.create_time < b.create_time) {
            return 1;
          }
        }
        return 0;
      });

      profile.checkouts.forEach((co: ICheckout) => {
        if (!usedISBNs[co.isbn]) {
          usedISBNs[co.isbn] = true;
          prioritizedISBNs.push(co.isbn);
        }
      });
    }

    // fill up with fallbacks
    fallbackIsbns.forEach((fbIsbn: string) => {
      if (!usedISBNs[fbIsbn]) {
        usedISBNs[fbIsbn] = true;
        prioritizedISBNs.push(fbIsbn);
      }
    });

    // but the ISBNs in prefered view order
    INDEX_ORDER.forEach((newIndex: number, index: number) => {
      results[newIndex] = prioritizedISBNs[index];
    });

    // filter truthy results and limit number of results
    return results.filter((isbn: any) => !!isbn).slice(0, RECENT_READ_ID_COUNT);
  };
}

export function loadMinutesReadTodayAndForever() {
  return (dispatch: any, getState: any) => {
    const currentUser = firebaseApp.auth().currentUser;
    const db = firebaseApp.database();

    if (currentUser) {
      const createDate = DateTime.fromHTTP(currentUser.metadata.creationTime!);
      if (createDate.isValid) {
        const dur = DateTime.local().diff(createDate, ['years', 'days']);

        if (dur.isValid) {
          dispatch(
            setStatsDuration({
              days: Math.round(dur.days),
              total_days: Math.round(dur.as('days')),
              years: Math.round(dur.years),
            })
          );
        } else {
          console.error(
            `[loadMinutesReadTodayAndForever] Error while trying to to calculate ` +
              `the duration time since the Firebase user was created: ${
                createDate.invalidReason
              }\n`
          );
        }
      } else {
        console.error(
          `[loadMinutesReadTodayAndForever] Error while trying to parse the ` +
            `creation time of a Firebase user: ${createDate.invalidReason}\n`
        );
      }
    }

    if (currentUser && currentUser.uid) {
      const today = DateTime.local();
      const todayKey = today.toFormat('y-MM-dd');

      const pathToday =
        paths.database.userReadingStats(
          currentUser.uid,
          StatsGranularity.DAYS
        ) + `/${todayKey}`;
      const refReadToday: Reference = db.ref(pathToday);
      refReadToday
        .once('value')
        .then((snap: DataSnapshot) => dispatch(setStatToday(snap.val())));

      const pathAllTime = paths.database.userReadingStats(
        currentUser.uid,
        StatsGranularity.ALL_TIME
      );
      const refReadAllTime: Reference = db.ref(pathAllTime);
      refReadAllTime
        .once('value')
        .then((snap: DataSnapshot) => dispatch(setStatAllTime(snap.val())));
    }
  };
}

export function loadStats(resolution: StatsResolution) {
  return (dispatch: any, getState: any) => {
    const currentUser = firebaseApp.auth().currentUser;
    const db = firebaseApp.database();
    const currentState: IStatsState = getState().stats;

    if (rejectStatsLoad(resolution, currentState)) {
      return;
    }

    if (currentUser && currentUser.uid) {
      let path: string | null = null;
      let dur: Duration | null = null;
      let format: string = '';

      switch (resolution) {
        case StatsResolution.day:
          path = paths.database.userReadingStats(
            currentUser.uid,
            StatsGranularity.DAYS
          );
          dur = Duration.fromObject({ days: 31 });
          format = 'y-MM-dd';
          break;
        case StatsResolution.week:
          path = paths.database.userReadingStats(
            currentUser.uid,
            StatsGranularity.WEEKS
          );
          dur = Duration.fromObject({ years: 1 });
          format = 'kkkk-WW'; // TODO! ISO week number is used
          break;
      }

      if (path && dur) {
        dispatch(setStatsLoadIsBusy(resolution));

        const queryStartDate = DateTime.local().minus(dur);
        const fromQueryVal = queryStartDate.toFormat(format);

        const ref: Reference = db.ref(path);
        ref
          .orderByKey()
          .startAt(fromQueryVal)
          .once('value')
          .then((snap: DataSnapshot) => {
            if (snap.exists()) {
              const value = snap.val();

              switch (resolution) {
                case StatsResolution.day:
                  dispatch(setStatsDataDays(value));
                  break;
                case StatsResolution.week:
                  dispatch(setStatsDataWeeks(value));
                  break;
                default:
                  dispatch(
                    setStatsLoadError({
                      err: new Error(
                        `[loadStats] no Action for resolution: ${resolution};`
                      ),
                      res: resolution,
                    })
                  );
              }
            } else {
              dispatch(
                setStatsLoadError({
                  err: new Error(`There is no stats data on path: ${path}`),
                  res: resolution,
                })
              );
            }
          })
          .catch(err => {
            console.error(
              `[loadStats] failed loading data for resolution "${resolution}":`,
              err
            );

            dispatch(
              setStatsLoadError({
                err,
                res: resolution,
              })
            );
          });
      }
    }
  };
}

function rejectStatsLoad(res: StatsResolution, state: IStatsState): boolean {
  if (
    typeof state.data_load_state[`${res}_resolution_busy`] === 'undefined' ||
    typeof state.data_load_state[`${res}_resolution_loaded`] === 'undefined'
  ) {
    return false;
  }
  if (
    state.data_load_state[`${res}_resolution_busy`] ||
    state.data_load_state[`${res}_resolution_loaded`]
  ) {
    return true;
  }
  return false;
}



// WEBPACK FOOTER //
// ./src/reading-stats/services.ts